import apiClient from '../../shared/services/apiClient';
import {
    parseFeatureSchema,
    generateFeatureTemplate,
    validateFeaturesAgainstSchema
} from '../../shared/utils/productSchemaParser';

/**
 * 产品管理服务
 * 用于管理产品类型、产品套餐和定价
 */
export const productService = {
    // ==========================================
    // 产品类型相关
    // ==========================================

    /**
     * 获取所有产品类型
     * @returns {Promise} 产品类型列表
     */
    getProductTypes: async () => {
        try {
            const response = await apiClient.get('/api/admin/product-types');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // 产品列表和详情
    // ==========================================

    /**
     * 获取产品列表
     * @param {Object} params - 查询参数
     * @param {string} params.product_type - 产品类型代码 (dns, cdn)
     * @param {string} params.status - 状态 (active, inactive)
     * @returns {Promise} 产品列表
     */
    getProducts: async (params = {}) => {
        try {
            const response = await apiClient.get('/api/admin/products', { params });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 获取单个产品详情
     * @param {number} id - 产品ID
     * @returns {Promise} 产品详细信息
     */
    getProduct: async (id) => {
        try {
            const response = await apiClient.get(`/api/admin/products/get?id=${id}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // 产品管理操作
    // ==========================================

    /**
     * 创建产品
     * @param {Object} data - 产品数据
     * @param {number} data.product_type_id - 产品类型ID (1=DNS, 2=CDN)
     * @param {string} data.name - 产品名称
     * @param {string} data.slug - 产品标识符
     * @param {string} data.description - 描述
     * @param {Object} data.features - 产品特性配置（JSON对象）
     * @param {string} data.billing_type - 计费类型 (recurring, onetime)
     * @param {number} data.is_addon - 是否是附加产品 (0, 1)
     * @param {number} data.requires_product_id - 依赖的产品ID（附加产品必填）
     * @param {number} data.sort_order - 排序
     * @param {string} data.status - 状态 (active, inactive)
     * @param {Array<number>} data.dns_parent_group_ids - DNS产品绑定的父节点组
     * @param {Array} data.pricing - 定价信息数组
     * @returns {Promise} 创建结果
     */
    createProduct: async (data) => {
        try {
            const response = await apiClient.post('/api/admin/products/create', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 更新产品
     * @param {Object} data - 产品数据（包含id字段）
     * @param {number} data.id - 产品ID
     * @param {string} data.name - 产品名称
     * @param {string} data.slug - 产品标识符
     * @param {string} data.description - 描述
     * @param {Object} data.features - 产品特性配置（JSON对象）
     * @param {string} data.billing_type - 计费类型 (recurring, onetime)
     * @param {number} data.sort_order - 排序
     * @param {string} data.status - 状态 (active, inactive)
     * @param {Array} data.pricing - 定价信息数组（可选）
     * @returns {Promise} 更新结果
     */
    /** Product line name / description + translations (admin language tabs). */
    updateProductType: async (data) => {
        try {
            const response = await apiClient.post('/api/admin/product-types/update', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    updateProduct: async (data) => {
        try {
            const response = await apiClient.post('/api/admin/products/update', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 删除产品
     * @param {number} id - 产品ID
     * @returns {Promise} 删除结果
     */
    deleteProduct: async (id) => {
        try {
            const response = await apiClient.delete('/api/admin/products/delete', {
                data: { id }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // 常量定义
    // ==========================================

    /**
     * 产品类型枚举
     */
    PRODUCT_TYPES: {
        DNS: 'dns',
        CDN: 'cdn'
    },

    /**
     * 产品状态枚举
     */
    STATUS: {
        ACTIVE: 'active',
        INACTIVE: 'inactive'
    },

    /**
     * 计费类型枚举
     */
    BILLING_TYPES: {
        RECURRING: 'recurring',  // 周期性计费
        ONETIME: 'onetime'       // 一次性计费
    },

    /**
     * 计费周期枚举
     */
    BILLING_CYCLES: {
        MONTHLY: 'monthly',           // 月付
        QUARTERLY: 'quarterly',       // 季付
        SEMI_ANNUALLY: 'semi_annually', // 半年付
        ANNUALLY: 'annually'          // 年付
    },

    /**
     * 货币枚举
     */
    CURRENCIES: {
        CNY: 'CNY',
        USD: 'USD'
    },

    // ==========================================
    // 辅助函数
    // ==========================================

    /**
     * 根据产品类型获取可用的计费周期
     * @param {string} productType - 产品类型代码 (dns, cdn)
     * @returns {Array} 可用的计费周期选项
     */
    getBillingCyclesByProductType: (productType) => {
        const billingCyclesConfig = {
            dns: [
                { value: 'monthly', label: '月付', months: 1 },
                { value: 'quarterly', label: '季付', months: 3 },
                { value: 'annually', label: '年付', months: 12 }
            ],
            cdn: [
                { value: 'monthly', label: '月付', months: 1 },
                { value: 'quarterly', label: '季付', months: 3 },
                { value: 'semi_annually', label: '半年付', months: 6 },
                { value: 'annually', label: '年付', months: 12 }
            ]
        };

        return billingCyclesConfig[productType] || billingCyclesConfig.dns;
    },

    /**
     * 获取产品类型的feature_schema并解析为字段配置
     * @param {number} productTypeId - 产品类型ID
     * @returns {Promise<Array>} 字段配置数组
     */
    getFeatureFieldsByProductTypeId: async (productTypeId) => {
        try {
            const typesResponse = await productService.getProductTypes();
            if (!typesResponse.success) {
                throw new Error('获取产品类型失败');
            }

            const productType = typesResponse.data.find(t => t.id === productTypeId);
            if (!productType || !productType.feature_schema) {
                return [];
            }

            return parseFeatureSchema(productType.feature_schema);
        } catch (error) {
            console.error('获取特性字段失败:', error);
            throw error;
        }
    },

    /**
     * 根据产品类型code获取feature_schema并解析
     * @param {string} productTypeCode - 产品类型代码 (dns, cdn)
     * @returns {Promise<Array>} 字段配置数组
     */
    getFeatureFieldsByProductTypeCode: async (productTypeCode) => {
        try {
            const typesResponse = await productService.getProductTypes();
            if (!typesResponse.success) {
                throw new Error('获取产品类型失败');
            }

            const productType = typesResponse.data.find(t => t.code === productTypeCode);
            if (!productType || !productType.feature_schema) {
                return [];
            }

            return parseFeatureSchema(productType.feature_schema);
        } catch (error) {
            console.error('获取特性字段失败:', error);
            throw error;
        }
    },

    /**
     * 生成产品特性模板（从API获取的schema）
     * @param {number} productTypeId - 产品类型ID
     * @returns {Promise<Object>} 特性模板对象
     */
    getFeatureTemplateByProductTypeId: async (productTypeId) => {
        try {
            const typesResponse = await productService.getProductTypes();
            if (!typesResponse.success) {
                throw new Error('获取产品类型失败');
            }

            const productType = typesResponse.data.find(t => t.id === productTypeId);
            if (!productType || !productType.feature_schema) {
                return {};
            }

            return generateFeatureTemplate(productType.feature_schema);
        } catch (error) {
            console.error('生成特性模板失败:', error);
            throw error;
        }
    },

    /**
     * 验证产品特性配置（使用API的schema）
     * @param {Object} features - 特性配置对象
     * @param {number} productTypeId - 产品类型ID
     * @returns {Promise<Object>} { valid: boolean, errors: Array }
     */
    validateFeaturesByProductTypeId: async (features, productTypeId) => {
        try {
            const typesResponse = await productService.getProductTypes();
            if (!typesResponse.success) {
                throw new Error('获取产品类型失败');
            }

            const productType = typesResponse.data.find(t => t.id === productTypeId);
            if (!productType || !productType.feature_schema) {
                return { valid: true, errors: [] };
            }

            return validateFeaturesAgainstSchema(features, productType.feature_schema);
        } catch (error) {
            console.error('验证特性失败:', error);
            return {
                valid: false,
                errors: ['验证失败: ' + error.message]
            };
        }
    }
};

export default productService;
